import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, ShieldCheck } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from "@/components/ui/sheet";
import { NAV_LINKS } from "@/lib/content";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 z-50 w-full transition-colors duration-500 ${
                scrolled
                    ? "border-b border-white/10 bg-navy-950/70 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
            data-testid="navbar"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-12">
                {/* Brand */}
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="flex items-center gap-3"
                    data-testid="nav-brand"
                >
                    <img
                        src="/heir-mark-gold.svg"
                        alt="HEIR"
                        className="h-8 w-8 object-contain"
                    />
                    <span className="font-display text-lg tracking-[0.35em] text-white">
                        HEIR
                    </span>
                </button>

                {/* Desktop links */}
                <nav className="hidden items-center gap-7 lg:flex">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            onClick={() => scrollTo(link.id)}
                            data-testid={`nav-link-${link.id}`}
                            className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 transition-colors duration-300 hover:text-gold"
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* Confidential badge + mobile trigger */}
                <div className="flex items-center gap-4">
                    <span className="hidden items-center gap-2 border border-white/10 px-3 py-1.5 md:flex">
                        <ShieldCheck className="h-3 w-3 text-gold" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                            Confidential
                        </span>
                    </span>

                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                className="lg:hidden text-white"
                                data-testid="nav-mobile-trigger"
                                aria-label="Open menu"
                            >
                                <Menu className="h-6 w-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="w-[280px] border-l border-white/10 bg-navy-950 p-0"
                        >
                            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
                                <span className="font-display text-lg tracking-[0.35em] text-white">
                                    HEIR
                                </span>
                                <button
                                    onClick={() => setOpen(false)}
                                    aria-label="Close menu"
                                >
                                    <X className="h-5 w-5 text-white/70" />
                                </button>
                            </div>
                            <nav className="flex flex-col px-6 py-4">
                                {NAV_LINKS.map((link) => (
                                    <button
                                        key={link.id}
                                        onClick={() => scrollTo(link.id)}
                                        data-testid={`nav-mobile-link-${link.id}`}
                                        className="border-b border-white/5 py-4 text-left font-mono text-xs uppercase tracking-[0.18em] text-white/70 transition-colors hover:text-gold"
                                    >
                                        {link.label}
                                    </button>
                                ))}
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </motion.header>
    );
}
